import { useState, useCallback, useEffect, useMemo } from 'react';

import * as DialogPrimitive from '@radix-ui/react-dialog';
import { FileSpreadsheet, FolderOpen, Loader2, Search, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { apiClient } from '@/lib/api-client';

import type { ResourcePickerConfig, SelectedResource, ConnectionAccessResponse } from '@optidev/shared-types';

interface DriveItem {
  id: string;
  name: string;
  webUrl?: string;
  size?: number;
  lastModifiedDateTime?: string;
  file?: { mimeType?: string };
  parentReference?: {
    driveId?: string;
    driveType?: string;
    id?: string;
    path?: string;
  };
}

interface MicrosoftPickerProps {
  config: ResourcePickerConfig;
  accessToken?: string;
  connectionId?: string;
  getAuthToken?: () => Promise<string | null>;
  onSelect: (resources: SelectedResource[]) => void;
  disabled?: boolean;
  buttonLabel?: string;
  currentSelectionCount?: number;
}

const GRAPH_BASE = 'https://graph.microsoft.com/v1.0';
const EXCEL_MIME_TYPES = new Set([
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
  'application/vnd.ms-excel.sheet.macroEnabled.12',
]);

function isExcelFile(item: DriveItem, filters: string[]): boolean {
  if (!item.file) return false;
  const mime = item.file.mimeType;
  if (mime && EXCEL_MIME_TYPES.has(mime)) return true;
  const lowerName = item.name.toLowerCase();
  return filters.some((ext) => lowerName.endsWith(ext.toLowerCase()));
}

function formatSize(bytes?: number): string {
  if (!bytes || bytes <= 0) return '';
  const units = ['B', 'KB', 'MB', 'GB'];
  let value = bytes;
  let unitIdx = 0;
  while (value >= 1024 && unitIdx < units.length - 1) {
    value /= 1024;
    unitIdx++;
  }
  return `${value.toFixed(1)} ${units[unitIdx]}`;
}

/**
 * MicrosoftPicker - Custom Excel file picker via Microsoft Graph API
 *
 * Renders an in-app modal listing the user's Excel workbooks (fetched directly
 * from Graph) so the user selects which ones to grant the agent access to,
 * before the connection is finalized. Uses the OAuth access token issued during
 * the connector sign-in flow — no third-party SDK required.
 *
 * @see https://learn.microsoft.com/en-us/graph/api/driveitem-search
 */
export function MicrosoftPicker({
  config,
  accessToken: accessTokenProp,
  connectionId,
  getAuthToken,
  onSelect,
  disabled,
  buttonLabel = 'Select Workbook',
  currentSelectionCount = 0,
}: MicrosoftPickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isFetchingToken, setIsFetchingToken] = useState(false);
  const [fetchedToken, setFetchedToken] = useState<string | null>(null);
  const [tokenError, setTokenError] = useState<string | null>(null);
  const [items, setItems] = useState<DriveItem[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoadingFiles, setIsLoadingFiles] = useState(false);
  const [filesError, setFilesError] = useState<string | null>(null);
  const [hasAutoOpened, setHasAutoOpened] = useState(false);

  const accessToken = accessTokenProp || fetchedToken;
  const filters = useMemo(
    () => config.microsoft?.filters || ['.xlsx', '.xls', '.xlsm'],
    [config.microsoft?.filters],
  );
  const allowMultiple = config.multiple !== false;
  const maxSelection = config.maxSelection;

  const fetchAccessToken = useCallback(async (): Promise<string | null> => {
    if (!connectionId || !getAuthToken) return null;

    try {
      setIsFetchingToken(true);
      setTokenError(null);
      const authToken = await getAuthToken();
      if (!authToken) {
        setTokenError('Authentication required');
        return null;
      }

      const { data: accessData } = await apiClient.post<ConnectionAccessResponse>(
        `/api/connections/${connectionId}/access`,
        {},
        { headers: { Authorization: `Bearer ${authToken}` } },
      );

      const token = accessData.data?.accessToken as string | undefined;
      if (token) {
        setFetchedToken(token);
        return token;
      }
      return null;
    } catch (error) {
      console.error('[MicrosoftPicker] Failed to fetch access token:', error);
      const axiosError = error as { response?: { data?: { message?: string } } };
      const errorMessage = axiosError.response?.data?.message;
      if (errorMessage?.includes('refresh') || errorMessage?.includes('re-authenticate')) {
        setTokenError('Session expired. Please click Reauthorize above to reconnect.');
      } else {
        setTokenError('Failed to load. Please try again or click Reauthorize.');
      }
      return null;
    } finally {
      setIsFetchingToken(false);
    }
  }, [connectionId, getAuthToken]);

  const loadFiles = useCallback(
    async (initialToken: string, query: string) => {
      setIsLoadingFiles(true);
      setFilesError(null);

      const fetchOnce = async (token: string): Promise<Response> => {
        // Use search with .xlsx as the default query so we list workbooks; when the
        // user types a query, search by that term and filter by extension client-side.
        const trimmed = query.trim();
        const searchTerm = trimmed.length > 0 ? trimmed : '.xlsx';
        const url = `${GRAPH_BASE}/me/drive/search(q='${encodeURIComponent(searchTerm)}')?$top=50&$select=id,name,webUrl,size,lastModifiedDateTime,file,parentReference`;
        return fetch(url, {
          headers: { Authorization: `Bearer ${token}` },
        });
      };

      try {
        let response = await fetchOnce(initialToken);

        // On 401, the access token likely expired. Refetch via the connections
        // API (which transparently uses the refresh token) and retry once.
        if (response.status === 401 && connectionId && getAuthToken) {
          const refreshed = await fetchAccessToken();
          if (refreshed) {
            response = await fetchOnce(refreshed);
          }
        }

        if (!response.ok) {
          const text = await response.text().catch(() => '');
          throw new Error(`Graph API ${response.status}: ${text || response.statusText}`);
        }

        const data = (await response.json()) as { value: DriveItem[] };
        const excelItems = (data.value || []).filter((item) => isExcelFile(item, filters));
        setItems(excelItems);
      } catch (error) {
        console.error('[MicrosoftPicker] Failed to fetch files:', error);
        setFilesError('Unable to load workbooks. Please try again.');
        setItems([]);
      } finally {
        setIsLoadingFiles(false);
      }
    },
    [filters, connectionId, getAuthToken, fetchAccessToken],
  );

  // Load files when modal opens
  useEffect(() => {
    if (!isOpen || !accessToken) return;
    void loadFiles(accessToken, searchQuery);
    // We intentionally only reload on isOpen + token change here; the search
    // effect below handles query updates.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, accessToken]);

  // Debounced search refresh
  useEffect(() => {
    if (!isOpen || !accessToken) return;
    const handle = setTimeout(() => {
      void loadFiles(accessToken, searchQuery);
    }, 300);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  const openModal = useCallback(async () => {
    setSelectedIds(new Set());
    setSearchQuery('');
    setFilesError(null);

    if (!accessToken) {
      const token = await fetchAccessToken();
      if (!token) return;
    }
    setIsOpen(true);
  }, [accessToken, fetchAccessToken]);

  // Auto-open after OAuth completes when no resources are selected yet
  useEffect(() => {
    if (hasAutoOpened) return;
    if (!accessToken) return;
    if (currentSelectionCount > 0) return;

    setHasAutoOpened(true);
    setTimeout(() => {
      void openModal();
    }, 300);
  }, [accessToken, currentSelectionCount, hasAutoOpened, openModal]);

  const toggleItem = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        if (!allowMultiple) next.clear();
        if (maxSelection && next.size >= maxSelection) return prev;
        next.add(id);
      }
      return next;
    });
  };

  const handleConfirm = () => {
    const selected = items.filter((item) => selectedIds.has(item.id));
    const resources: SelectedResource[] = selected.map((item) => ({
      id: item.id,
      name: item.name,
      url: item.webUrl,
      mimeType: item.file?.mimeType,
      provider: 'microsoft' as const,
      metadata: {
        driveId: item.parentReference?.driveId,
        parentId: item.parentReference?.id,
        driveType: item.parentReference?.driveType,
        size: item.size,
      },
    }));
    onSelect(resources);
    setIsOpen(false);
  };

  const canConfirm = selectedIds.size > 0;
  const canOpen = (accessToken || (connectionId && getAuthToken)) && !tokenError;

  return (
    <div className="space-y-2">
      <Button
        type="button"
        variant="outline"
        onClick={openModal}
        disabled={disabled || isFetchingToken || !canOpen}
        className="w-full"
      >
        {isFetchingToken ? (
          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
        ) : (
          <FolderOpen className="h-4 w-4 mr-2" />
        )}
        {isFetchingToken ? 'Loading...' : buttonLabel}
      </Button>

      {tokenError && <p className="text-sm text-destructive">{tokenError}</p>}

      <DialogPrimitive.Root open={isOpen} onOpenChange={setIsOpen}>
        <DialogPrimitive.Portal>
          {/* Overlay/content z-[10001] to render above the parent connection modal at z-[9999] */}
          <DialogPrimitive.Overlay className="fixed inset-0 z-[10000] bg-black/60 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
          <DialogPrimitive.Content className="fixed left-[50%] top-[50%] z-[10001] grid w-full max-w-3xl translate-x-[-50%] translate-y-[-50%] gap-4 bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 sm:rounded-lg">
            <div className="flex flex-col space-y-1.5 text-center sm:text-left">
              <DialogPrimitive.Title className="text-lg font-semibold leading-none tracking-tight">
                Select an Excel workbook
              </DialogPrimitive.Title>
            </div>
            <DialogPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none">
              <X className="h-4 w-4" />
              <span className="sr-only">Close</span>
            </DialogPrimitive.Close>

          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search your OneDrive workbooks"
              className="pl-9"
            />
          </div>

          <ScrollArea className="h-[420px] -mx-2 px-2">
            {isLoadingFiles ? (
              <div className="flex items-center justify-center h-full text-muted-foreground">
                <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                Loading workbooks...
              </div>
            ) : filesError ? (
              <div className="p-3 rounded-md bg-destructive/10 border border-destructive/20 space-y-2">
                <p className="text-sm text-destructive font-medium">Unable to load workbooks</p>
                <p className="text-sm text-destructive/80">{filesError}</p>
                {accessToken && (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => void loadFiles(accessToken, searchQuery)}
                  >
                    Retry
                  </Button>
                )}
              </div>
            ) : items.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-muted-foreground gap-2">
                <FileSpreadsheet className="h-8 w-8" />
                <p className="text-sm">No Excel workbooks found</p>
                {searchQuery && (
                  <p className="text-xs">Try a different search term</p>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {items.map((item) => {
                  const isSelected = selectedIds.has(item.id);
                  return (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => toggleItem(item.id)}
                      className={`flex items-start gap-3 p-3 rounded-md border text-left transition-colors ${
                        isSelected
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:bg-muted/50'
                      }`}
                    >
                      <Checkbox
                        checked={isSelected}
                        className="mt-0.5 pointer-events-none"
                      />
                      <FileSpreadsheet className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium truncate">{item.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {formatSize(item.size)}
                        </p>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </ScrollArea>

            <div className="flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2">
              <Button type="button" variant="outline" onClick={() => setIsOpen(false)}>
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleConfirm}
                disabled={!canConfirm}
              >
                Select
                {selectedIds.size > 0 && ` (${selectedIds.size})`}
              </Button>
            </div>
          </DialogPrimitive.Content>
        </DialogPrimitive.Portal>
      </DialogPrimitive.Root>
    </div>
  );
}

export default MicrosoftPicker;
